import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { fetchAccountMe, fetchSbpBanks, patchAccountMe } from "../api/client.js";
import { useLayoutTitle } from "../context/LayoutTitleContext.jsx";
import {
  cacheGlobalProfileFromAccountMe,
  isVitrineLoggedIn,
} from "../lib/wicoopProfileStorage.js";
import { accountNeedsProfileCompletion } from "../utils/profileCompletion.js";
import styles from "./ParticipatePage.module.css";

export default function ProfileCompletePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/profile";
  const { setPageTitle } = useLayoutTitle();

  const [loading, setLoading] = useState(true);
  const [jumpEnabled, setJumpEnabled] = useState(false);
  const [banks, setBanks] = useState([]);
  const [phase, setPhase] = useState("last_name");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [sbpBankId, setSbpBankId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const steps = useMemo(
    () => (jumpEnabled ? ["last_name", "first_name", "bank"] : ["last_name", "first_name"]),
    [jumpEnabled]
  );

  useEffect(() => {
    setPageTitle("Профиль");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [me, sbp] = await Promise.all([fetchAccountMe(), fetchSbpBanks()]);
        if (cancelled) return;
        const jump = !!sbp.jump_enabled;
        setJumpEnabled(jump);
        setBanks(Array.isArray(sbp.items) ? sbp.items : []);
        setFirstName((me.first_name || "").trim());
        setLastName((me.last_name || "").trim());
        setSbpBankId(me.sbp_bank_id != null ? String(me.sbp_bank_id) : "");
        if (!accountNeedsProfileCompletion(me, jump)) {
          navigate(returnTo, { replace: true });
          return;
        }
        if (!(me.last_name || "").trim()) {
          setPhase("last_name");
        } else if (!(me.first_name || "").trim()) {
          setPhase("first_name");
        } else if (jump && (me.sbp_bank_id == null || me.sbp_bank_id === "")) {
          setPhase("bank");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Не удалось загрузить профиль");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, returnTo]);

  if (!isVitrineLoggedIn()) {
    return <Navigate to="/profile/login" replace />;
  }

  if (loading) {
    return <p className={styles.loading}>Загрузка…</p>;
  }

  const stepIndex = steps.indexOf(phase);
  const stepLabel =
    stepIndex >= 0 ? `Профиль · шаг ${stepIndex + 1} из ${steps.length}` : "Профиль";

  async function handleSubmit() {
    setError(null);
    if (!lastName.trim() || !firstName.trim()) {
      setError("Укажите фамилию и имя.");
      return;
    }
    if (jumpEnabled && !sbpBankId) {
      setError("Выберите банк для перевода по СБП.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };
      if (jumpEnabled && sbpBankId) {
        payload.sbp_bank_id = Number(sbpBankId);
      }
      const me = await patchAccountMe(payload);
      cacheGlobalProfileFromAccountMe(me);
      navigate(returnTo, { replace: true });
    } catch (e) {
      setError(e.message || "Не удалось сохранить профиль");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`${styles.page} ${styles.pageSteps}`}>
      <div className={styles.card}>
        <p className={styles.stepLabel}>{stepLabel}</p>

        {phase === "last_name" && (
          <>
            <h2 className={styles.stepTitle}>Фамилия</h2>
            <p className={styles.stepBody}>
              Как в паспорте или как в банке для выплат — без сокращений.
            </p>
            <label className={styles.fieldLabel} htmlFor="profile-complete-last">
              Фамилия
            </label>
            <input
              id="profile-complete-last"
              className={styles.input}
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </>
        )}

        {phase === "first_name" && (
          <>
            <h2 className={styles.stepTitle}>Имя</h2>
            <p className={styles.stepBody}>Имя для заявок и уведомлений.</p>
            <label className={styles.fieldLabel} htmlFor="profile-complete-first">
              Имя
            </label>
            <input
              id="profile-complete-first"
              className={styles.input}
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </>
        )}

        {phase === "bank" && (
          <>
            <h2 className={styles.stepTitle}>Банк для СБП</h2>
            <p className={styles.stepBody}>
              Выплата идёт через СБП на ваш номер — выберите банк из списка.
            </p>
            <label className={styles.fieldLabel} htmlFor="profile-complete-bank">
              Банк
            </label>
            <select
              id="profile-complete-bank"
              className={styles.select}
              value={sbpBankId}
              onChange={(e) => setSbpBankId(e.target.value)}
              disabled={!banks.length}
            >
              {!banks.length && <option value="">Загрузка банков…</option>}
              {banks.length > 0 && <option value="">— выберите банк —</option>}
              {banks.map((b) => (
                <option key={b.value} value={String(b.value)}>
                  {b.title}
                </option>
              ))}
            </select>
          </>
        )}

        {error ? (
          <p className={styles.regError} role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className={phase === "last_name" ? `${styles.nav} ${styles.navSingle}` : styles.nav}>
        {phase !== "last_name" ? (
          <button
            type="button"
            className={styles.btnSecondary}
            disabled={busy}
            onClick={() => {
              setError(null);
              if (phase === "first_name") setPhase("last_name");
              else if (phase === "bank") setPhase("first_name");
            }}
          >
            Назад
          </button>
        ) : null}
        {phase === "last_name" ? (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={busy || !lastName.trim()}
            onClick={() => {
              setError(null);
              setPhase("first_name");
            }}
          >
            Далее
          </button>
        ) : null}
        {phase === "first_name" ? (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={busy || !firstName.trim()}
            onClick={() => {
              setError(null);
              if (jumpEnabled) setPhase("bank");
              else handleSubmit();
            }}
          >
            {jumpEnabled ? "Далее" : busy ? "Сохранение…" : "Готово"}
          </button>
        ) : null}
        {phase === "bank" ? (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={busy || !sbpBankId}
            onClick={handleSubmit}
          >
            {busy ? "Сохранение…" : "Готово"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
